import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { useLanguage } from '../components/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  User, 
  ArrowLeft, 
  Heart,
  MessageCircle,
  Share2,
  Camera,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Blog posts data (same as in Blog.jsx)
const blogPosts = [
  {
    id: 1,
    title_he: 'המהפכה של תמונות AI באירועים: מה שאתה צריך לדעת',
    title_en: 'The AI Photo Revolution at Events: What You Need to Know',
    excerpt_he: 'גלה כיצד תמונות AI משנות את עולם האירועים והצילומים, ומדוע זהו העתיד של הצילום המקצועי.',
    excerpt_en: 'Discover how AI photos are changing the world of events and photography, and why this is the future of professional photography.',
    image: '/blog/ai-revolution.jpg',
    author: 'דניאל כהן',
    author_en: 'Daniel Cohen',
    date: '2024-01-15',
    readTime: '5 דקות',
    readTime_en: '5 min read',
    category: 'טכנולוגיה',
    category_en: 'Technology',
    likes: 234,
    comments: 45,
    featured: true,
    content_he: `
# המהפכה של תמונות AI באירועים: מה שאתה צריך לדעת

עולם הצילום באירועים עובר מהפכה דיגיטלית מסחררת. טכנולוגיית הבינה המלאכותית משנה את הדרך שבה אנו תופסים רגעים, יוצרים זיכרונות וחולקים חוויות. במאמר זה, נחקור כיצד תמונות AI משפיעות על תעשיית האירועים ומדוע זהו העתיד של הצילום המקצועי.

## מהן תמונות AI?

תמונות AI הן תמונות שנוצרות או משופרות באמצעות אלגוריתמים של בינה מלאכותית. הטכנולוגיה הזו מאפשרת לנו ליצור תמונות מרהיבות באיכות מקצועית, לשנות רקעים, להוסיף אפקטים מיוחדים ואף להפוך אנשים לדמויות שונות.

## היתרונות באירועים

### 1. חוויה אינטראקטיבית
תמונות AI הופכות את הצילום באירועים לחוויה אינטראקטיבית ומהנה. האורחים יכולים לבחור דמויות, לשנות סגנונות ולקבל תמונות ייחודיות בזמן אמת.

### 2. איכות מקצועית
האלגוריתמים המתקדמים מבטיחים איכות תמונה גבוהה, תיקוני צבע אוטומטיים ושיפורים אסתטיים.

### 3. זמן אמת
התמונות מוכנות מיד לאחר הצילום, מה שמאפשר לאורחים לקבל ולשתף אותן ברגע.

## העתיד של צילום האירועים

הטכנולוגיה ממשיכה להתפתח במהירות. בעתיד, נראה יכולות מתקדמות יותר כמו:
- וידאו AI בזמן אמת
- מציאות רבודה משולבת
- התאמה אישית מתקדמת

## סיכום

תמונות AI הן לא רק טרנד חולף, אלא העתיד של צילום האירועים. הן מציעות חוויה טובה יותר לאורחים, תוצאות מקצועיות וערך מוסף לאירוע.
    `,
    content_en: `
# The AI Photo Revolution at Events: What You Need to Know

The world of event photography is undergoing a digital revolution. Artificial intelligence technology is changing the way we capture moments, create memories, and share experiences. In this article, we'll explore how AI photos are impacting the events industry and why this is the future of professional photography.

## What Are AI Photos?

AI photos are images that are created or enhanced using artificial intelligence algorithms. This technology allows us to create stunning images with professional quality, change backgrounds, add special effects, and even transform people into different characters.

## Benefits for Events

### 1. Interactive Experience
AI photos turn event photography into an interactive and enjoyable experience. Guests can choose characters, change styles, and get unique photos in real-time.

### 2. Professional Quality
Advanced algorithms ensure high image quality, automatic color corrections, and aesthetic enhancements.

### 3. Real-Time Results
Photos are ready immediately after capture, allowing guests to receive and share them instantly.

## The Future of Event Photography

Technology continues to evolve rapidly. In the future, we'll see more advanced capabilities like:
- Real-time AI video
- Augmented reality integration
- Advanced personalization

## Conclusion

AI photos are not just a passing trend, but the future of event photography. They offer a better experience for guests, professional results, and added value to the event.
    `
  },
  {
    id: 2,
    title_he: '10 טיפים לתמונות מושלמות בעמדת הצילום שלנו',
    title_en: '10 Tips for Perfect Photos at Our Photo Booth',
    excerpt_he: 'קבל את התוצאות הטובות ביותר מהחוויה שלנו עם הטיפים המקצועיים שלנו לתמונות מדהימות.',
    excerpt_en: 'Get the best results from our experience with our professional tips for amazing photos.',
    image: '/blog/perfect-photos.jpg',
    author: 'שרה לוי',
    author_en: 'Sarah Levi',
    date: '2024-01-10',
    readTime: '3 דקות',
    readTime_en: '3 min read',
    category: 'מדריכים',
    category_en: 'Guides',
    likes: 189,
    comments: 23,
    content_he: `
# 10 טיפים לתמונות מושלמות בעמדת הצילום שלנו

כדי לקבל את התוצאות הטובות ביותר מהחוויה שלנו, הכנו עבורכם 10 טיפים מקצועיים שיעזרו לכם ליצור תמונות מדהימות.

## 1. בחרו את הדמות הנכונה
כל דמות מתאימה לסגנון אחר. חשבו על האירוע ועל האנרגיה שאתם רוצים להעביר.

## 2. הביטוי הוא הכל
חיוך אמיתי, הבעה רצינית, או מבט משוגע - הביטוי שלכם הוא מה שהופך את התמונה למיוחדת.

## 3. תנועה ופוזה
אל תפחדו לזוז ולהתנסות בפוזות שונות. תנועה קטנה יכולה להפוך תמונה טובה למדהימה.

## 4. תאורה טובה
עמדו במקום מואר היטב. התאורה הטובה היא המפתח לתמונות איכותיות.

## 5. קבוצות וחברים
צלמו עם חברים! תמונות קבוצתיות תמיד מצליחות ויוצרות זיכרונות משותפים.

## 6. אביזרים ורקעים
נצלו את האפשרויות השונות של עמדת הצילום שלנו.

## 7. התמקדו במצלמה
הסתכלו ישר למצלמה כדי ליצור קשר עם המתבונן.

## 8. צלמו הרבה
אל תפחדו לצלם כמה תמונות. ככל שתצלמו יותר, הסיכוי לתמונה מושלמת גדל.

## 9. התפארו בתוצאה
הפגינו ביטחון עצמי! הביטחון משתקף בתמונה.

## 10. כיף והנאה
הדבר החשוב ביותר - תהנו מהחוויה!

## סיכום

עם הטיפים האלה, אתם מוכנים ליצור תמונות מדהימות שיזכרו לנצח.
    `,
    content_en: `
# 10 Tips for Perfect Photos at Our Photo Booth

To get the best results from our experience, we've prepared 10 professional tips to help you create amazing photos.

## 1. Choose the Right Character
Each character fits a different style. Think about the event and the energy you want to convey.

## 2. Expression is Everything
A genuine smile, serious expression, or crazy look - your expression is what makes the photo special.

## 3. Movement and Posing
Don't be afraid to move and try different poses. Small movement can turn a good photo into an amazing one.

## 4. Good Lighting
Stand in a well-lit place. Good lighting is the key to quality photos.

## 5. Groups and Friends
Shoot with friends! Group photos always succeed and create shared memories.

## 6. Accessories and Backgrounds
Take advantage of the different options our photo booth offers.

## 7. Focus on the Camera
Look straight at the camera to create a connection with the viewer.

## 8. Shoot a Lot
Don't be afraid to take several photos. The more you shoot, the greater the chance for a perfect photo.

## 9. Be Proud of the Result
Show self-confidence! Confidence is reflected in the photo.

## 10. Fun and Enjoyment
The most important thing - enjoy the experience!

## Conclusion

With these tips, you're ready to create amazing photos that will be remembered forever.
    `
  }
  // Add more blog posts as needed...
];

// Additional blog posts data (matching Blog.jsx)
const additionalBlogPosts = [
  {
    id: 3,
    title_he: 'איך בוחרים את הדמות המושלמת לצילום?',
    title_en: 'How to Choose the Perfect Character for Your Photo?',
    excerpt_he: 'מדריך מקיף לבחירת הדמות המתאימה ביותר לסגנון שלך ולאירוע שלך.',
    excerpt_en: 'A comprehensive guide to choosing the most suitable character for your style and event.',
    image: '/blog/choose-character.jpg',
    author: 'יוסי גולן',
    author_en: 'Yossi Golan',
    date: '2024-01-05',
    readTime: '4 דקות',
    readTime_en: '4 min read',
    category: 'השראה',
    category_en: 'Inspiration',
    likes: 156,
    comments: 18,
    content_he: `
# איך בוחרים את הדמות המושלמת לצילום?

בחירת הדמות הנכונה היא אחד המרכיבים החשובים ביצירת תמונה מוצלחת. במדריך זה, נראה לכם איך לבחור את הדמות המושלמת שתתאים לסגנון שלכם ולאירוע.

## שלב 1: הבינו את סוג האירוע

### אירועים פורמליים
- חתונות: דמויות אלגנטיות כמו חתן וכלה, דמויות היסטוריות
- אירועי עסקים: דמויות מקצועיות, מנהלים, יזמים

### אירועים חברתיים
- מסיבות: דמויות כיפיות, סלבריטאים, דמויות מצחיקות
- ימי הולדת: דמויות מתאימות לגילאים

## שלב 2: התאמה לאישיות

### האם אתם רציניים או כיפיים?
- אנשים רציניים: דמויות מקצועיות, היסטוריות
- אנשים כיפיים: דמויות מצחיקות, פנטסטיות

## שלב 3: שיקולי קהל

### מי יראה את התמונות?
- משפחה: דמויות משפחתיות, חמודות
- חברים: דמויות טרנדיות, מודרניות
- לקוחות: דמויות מקצועיות

## טיפים נוספים

1. **נסו מספר דמויות** - אל תפחדו להתנסות
2. **שימו לב לפרטים** - הבגדים, האביזרים
3. **התאימו לרקע** - בדקו איך הדמות נראית עם הרקע
4. **בקשו חוות דעת** - שאלו חברים מהם חושבים

## סיכום

בחירת הדמות היא תהליך אישי שמשלב את האירוע, האישיות והקהל. עם ההנחיות האלה, תוכלו לבחור את הדמות המושלמת!
    `,
    content_en: `
# How to Choose the Perfect Character for Your Photo?

Choosing the right character is one of the most important components in creating a successful photo. In this guide, we'll show you how to choose the perfect character that fits your style and event.

## Step 1: Understand the Event Type

### Formal Events
- Weddings: Elegant characters like bride and groom, historical figures
- Business events: Professional characters, managers, entrepreneurs

### Social Events
- Parties: Fun characters, celebrities, funny characters
- Birthdays: Age-appropriate characters

## Step 2: Match Your Personality

### Are You Serious or Fun?
- Serious people: Professional characters, historical characters
- Fun people: Funny characters, fantasy characters

## Step 3: Audience Considerations

### Who Will See the Photos?
- Family: Family characters, cute characters
- Friends: Trendy characters, modern characters
- Clients: Professional characters

## Additional Tips

1. **Try Several Characters** - Don't be afraid to experiment
2. **Pay Attention to Details** - Clothes, accessories
3. **Match the Background** - Check how the character looks with the background
4. **Ask for Opinions** - Ask friends what they think

## Conclusion

Choosing a character is a personal process that combines the event, personality, and audience. With these guidelines, you can choose the perfect character!
    `
  },
  {
    id: 4,
    title_he: 'ההיסטוריה של תמונות הפוטו בות: מההתחלה ועד AI',
    title_en: 'The History of Photo Booths: From the Beginning to AI',
    excerpt_he: 'סיור מרתק בהתפתחות עמדות הצילום מהמצאתן ועד העידן הדיגיטלי המודרני.',
    excerpt_en: 'A fascinating tour of the evolution of photo booths from their invention to the modern digital age.',
    image: '/blog/photo-booth-history.jpg',
    author: 'משה כהן',
    author_en: 'Moshe Cohen',
    date: '2023-12-28',
    readTime: '6 דקות',
    readTime_en: '6 min read',
    category: 'היסטוריה',
    category_en: 'History',
    likes: 298,
    comments: 34,
    content_he: `
# ההיסטוריה של תמונות הפוטו בות: מההתחלה ועד AI

עמדות הצילום הן חלק מהתרבות הפופולרית כבר קרוב למאה שנים. הן התפתחו ממכונות מכניות פשוטות למערכות AI מתקדמות. בואו נסייר בהיסטוריה המרתקת הזו.

## הראשית: 1888-1920

### המצאת עמדת הצילום הראשונה
העמדה הראשונה הומצאה על ידי ויליאם פופ ב-1888. זו הייתה מכונה גדולה שצילמה תמונות בגודל כרטיס ביקור.

### התפתחות מוקדמת
- 1890: עמדות החלו להופיע בירידים ובשווקים
- 1900: הפופולריות גדלה בערים גדולות
- 1910: הופיעו העמדות הראשונות בארצות הברית

## תור הזהב: 1920-1960

### עמדות הצילום המודרניות
בשנות ה-20, עמדות הצילום הפכו פופולריות מאוד במקומות ציבוריים כמו תחנות רכבת, חנויות וחופי רחצה.

### מלחמת העולם השנייה
עמדות צילום שימשו לתיעוד חיילים לפני צאתם למלחמה.

## עידן הדיגיטל: 1960-2000

### המעבר לדיגיטל
בשנות ה-90, החל המעבר מצילום אנלוגי לדיגיטלי. זה שינה לחלוטין את האופן שבו עמדות הצילום עובדות.

### שיפורים טכנולוגיים
- מצלמות דיגיטליות
- מסכי מגע
- הדפסה מיידית

## המהפכה הדיגיטלית: 2000-2020

### עמדות צילום מתקדמות
העמדות הפכו מתוחכמות יותר עם:
- מסכים גדולים
- אפקטים מיוחדים
- רקעים ירוקים
- שיתוף ברשתות חברתיות

## עידן ה-AI: 2020-היום

### המהפכה של הבינה המלאכותית
היום, עמדות הצילום משתמשות בבינה מלאכותית ל:
- זיהוי פנים מתקדם
- שינוי רקעים בזמן אמת
- הפיכת אנשים לדמויות
- שיפור אוטומטי של תמונות

## העתיד

מה עוד צפוי?
- מציאות רבודה
- וידאו AI
- התאמה אישית מתקדמת
- אינטגרציה עם מדיה חברתית

## סיכום

ההיסטוריה של עמדות הצילום היא סיפור של חדשנות והתפתחות טכנולוגית. ממכונות פשוטות למערכות AI מתקדמות, הן ממשיכות להיות חלק חשוב מהתרבות שלנו.
    `,
    content_en: `
# The History of Photo Booths: From the Beginning to AI

Photo booths have been part of popular culture for nearly a century. They have evolved from simple mechanical machines to advanced AI systems. Let's tour this fascinating history.

## The Beginning: 1888-1920

### The Invention of the First Photo Booth
The first booth was invented by William Pope in 1888. It was a large machine that took business card-sized photos.

### Early Development
- 1890: Booths began appearing at fairs and markets
- 1900: Popularity grew in large cities
- 1910: The first booths appeared in the United States

## The Golden Age: 1920-1960

### Modern Photo Booths
In the 1920s, photo booths became very popular in public places like train stations, shops, and beaches.

### World War II
Photo booths were used to document soldiers before they went to war.

## The Digital Era: 1960-2000

### The Transition to Digital
In the 1990s, the transition from analog to digital photography began. This completely changed the way photo booths work.

### Technological Improvements
- Digital cameras
- Touch screens
- Instant printing

## The Digital Revolution: 2000-2020

### Advanced Photo Booths
Booths became more sophisticated with:
- Large screens
- Special effects
- Green screens
- Social media sharing

## The AI Era: 2020-Today

### The Artificial Intelligence Revolution
Today, photo booths use artificial intelligence for:
- Advanced face recognition
- Real-time background changes
- Transforming people into characters
- Automatic photo enhancement

## The Future

What else is expected?
- Augmented reality
- AI video
- Advanced personalization
- Social media integration

## Conclusion

The history of photo booths is a story of innovation and technological development. From simple machines to advanced AI systems, they continue to be an important part of our culture.
    `
  },
  {
    id: 5,
    title_he: 'מאחורי הקלעים: כך עובדת הטכנולוגיה שלנו',
    title_en: 'Behind the Scenes: How Our Technology Works',
    excerpt_he: 'הצצה לטכנולוגיה המתקדמת שמניעה את הקסם שלנו והופכת תמונות ליצירות אמנות.',
    excerpt_en: 'A peek into the advanced technology that powers our magic and turns photos into works of art.',
    image: '/blog/behind-tech.jpg',
    author: 'דניאל כהן',
    author_en: 'Daniel Cohen',
    date: '2023-12-20',
    readTime: '7 דקות',
    readTime_en: '7 min read',
    category: 'טכנולוגיה',
    category_en: 'Technology',
    likes: 412,
    comments: 56,
    content_he: `
# מאחורי הקלעים: כך עובדת הטכנולוגיה שלנו

אי פעם תהיתם איך הקסם שלנו עובד? איך אנחנו הופכים תמונה פשוטה ליצירת אמנות מדהימה? במאמר זה, נחשוף את הטכנולוגיה המתקדמת שעומדת מאחורי החוויה שלנו.

## הלב של המערכת: בינה מלאכותית

### זיהוי פנים מתקדם
המערכת שלנו משתמשת באלגוריתמים מתקדמים לזיהוי פנים:
- ניתוח 68 נקודות מפתח על הפנים
- זיהוי ביטויים ורגשות
- התאמה מדויקת לדמויות

### עיבוד תמונה בזמן אמת
כל תמונה עוברת עיבוד מתקדם:
- שיפור איכות אוטומטי
- תיקון צבעים חכם
- הפחתת רעשים

## המנוע הגרפי: רינדור באיכות גבוהה

### מודלים מתקדמים
אנחנו משתמשים במודלים הכי חדשניים בשוק:
- Flux לדיוק מרבי
- Stable Diffusion ליצירתיות
- מודלים ייעודיים לדמויות

### אופטימיזציה למהירות
המערכת מותאמת למהירות:
- עיבוד מקבילי
- דחיסה חכמה
- מטמון תוצאות

## הארכיטקטורה שלנו

### תשתית ענן חזקה
- שרתים במיקומים גיאוגרפיים מרובים
- גיבוי אוטומטי
- סקיילביליות מיידית

### אבטחה ופרטיות
- הצפנה מתקדמת
- מחיקת תמונות אוטומטית
- ציות ל-GDPR

## התהליך המלא

### 1. צילום התמונה
המצלמה לוכדת את התמונה באיכות גבוהה.

### 2. ניתוח AI
המערכת מנתחת את הפנים והביטוי.

### 3. התאמה לדמות
האלגוריתם מתאים את התמונה לדמות שנבחרה.

### 4. שיפורים סופיים
תיקונים אחרונים ושיפור איכות.

### 5. מסירה מיידית
התמונה מוכנה לשיתוף והורדה.

## העתיד שלנו

אנחנו מתכננים:
- וידאו AI בזמן אמת
- מציאות רבודה
- התאמה אישית עמוקה יותר

## סיכום

הטכנולוגיה שלנו היא שילוב של חדשנות, ביצועים וחוויית משתמש. אנחנו ממשיכים לדחוף את הגבולות ולהציע את החוויה הטובה ביותר ללקוחות שלנו.
    `,
    content_en: `
# Behind the Scenes: How Our Technology Works

Ever wondered how our magic works? How do we turn a simple photo into a stunning work of art? In this article, we'll reveal the advanced technology behind our experience.

## The Heart of the System: Artificial Intelligence

### Advanced Face Recognition
Our system uses advanced algorithms for face recognition:
- Analysis of 68 facial key points
- Detection of expressions and emotions
- Precise matching to characters

### Real-Time Image Processing
Every photo undergoes advanced processing:
- Automatic quality enhancement
- Smart color correction
- Noise reduction

## The Graphics Engine: High-Quality Rendering

### Advanced Models
We use the most innovative models on the market:
- Flux for maximum precision
- Stable Diffusion for creativity
- Character-specific models

### Speed Optimization
The system is optimized for speed:
- Parallel processing
- Smart compression
- Result caching

## Our Architecture

### Strong Cloud Infrastructure
- Servers in multiple geographic locations
- Automatic backup
- Immediate scalability

### Security and Privacy
- Advanced encryption
- Automatic photo deletion
- GDPR compliance

## The Complete Process

### 1. Photo Capture
The camera captures the photo in high quality.

### 2. AI Analysis
The system analyzes the face and expression.

### 3. Character Matching
The algorithm matches the photo to the chosen character.

### 4. Final Enhancements
Final adjustments and quality improvement.

### 5. Instant Delivery
The photo is ready for sharing and download.

## Our Future

We're planning:
- Real-time AI video
- Augmented reality
- Deeper personalization

## Conclusion

Our technology is a combination of innovation, performance, and user experience. We continue to push boundaries and offer the best experience for our customers.
    `
  },
  {
    id: 6,
    title_he: 'חתונות, בר מצוות ואירועים: רעיונות לצילומים בלתי נשכחים',
    title_en: 'Weddings, Bar Mitzvahs and Events: Ideas for Unforgettable Photos',
    excerpt_he: 'השראה ורעיונות יצירתיים להפוך את האירוע שלך לחוויה צילומית מיוחדת.',
    excerpt_en: 'Inspiration and creative ideas to make your event a special photographic experience.',
    image: '/blog/events-ideas.jpg',
    author: 'שרה לוי',
    author_en: 'Sarah Levi',
    date: '2023-12-15',
    readTime: '5 דקות',
    readTime_en: '5 min read',
    category: 'אירועים',
    category_en: 'Events',
    likes: 567,
    comments: 89,
    featured: true,
    content_he: `
# חתונות, בר מצוות ואירועים: רעיונות לצילומים בלתי נשכחים

האירוע שלך צריך להיות מיוחד, והתמונות צריכות לשקף את הקסם של הרגע. במדריך זה, נציג רעיונות יצירתיים להפוך כל אירוע לחוויה צילומית בלתי נשכחת.

## חתונות: רגעים של אהבה

### רעיונות לדמויות
- **חתן וכלה מלכותיים** - שיער מוזהב, כתרים, בגדים מלכותיים
- **זוג אגדותי** - נסיך ונסיכה מאגדות אהבה
- **כוכבי קולנוע** - זוג מפורסם מהוליווד הישנה
- **זוג עתידני** - חליפות חלל, סגנון מדע בדיוני

### זמנים מומלצים לצילומים
- **טקס החתונה** - לפני או אחרי הטקס
- **קבלת הפנים** - בהגעת האורחים
- **הריקוד הראשון** - רגע רומנטי
- **חתיכת העוגה** - רגע של שמחה

## בר מצוות: צעד לבגרות

### דמויות מתאימות
- **לוחם יהודי** - דוד המלך, המכבים
- **חכם תלמודי** - רמב"ם, הגאון מווילנה
- **גיבור ספורט** - שחקן כדורגל, שחיין אולימפי
- **כוכב רוק** - גיטריסט, סולן להקה

### רעיונות מיוחדים
- **תמונות קבוצתיות** - עם כל החברים כדמויות שונות
- **משפחה** - כל המשפחה בדמויות תואמות
- **מורים** - תודה למורים בדמויות מיוחדות

## אירועי חברה: כיף ובידור

### אירועי חברה
- **צוות גיבורי על** - כל איש צוות כגיבור שונה
- **כוכבי קולנוע** - צוות כסלבריטאים מפורסמים
- **דמויות מקצועיות** - רופאים, עורכי דין, מהנדסים

### ימי הולדת
- **ילדים** - גיבורי על, נסיכות, דמויות מסרטים
- **מבוגרים** - כוכבי נעורים, דמויות נוסטלגיות
- **גיל הזהב** - כוכבי עבר, דמויות קלאסיות

## טיפים להצלחה

### הכנה מראש
- בחרו דמויות שמתאימות לאירוע
- הכינו רשימת אורחים
- תכננו את הזמנים

### במהלך האירוע
- הדריכו את האורחים
- צלמו הרבה תמונות
- שימו לב לפרטים

### אחרי האירוע
- שתפו את התמונות
- צרו אלבום מיוחד
- שמרו את הזיכרונות

## סיכום

עם הרעיונות האלה, האירוע שלך יהפוך לחוויה צילומית בלתי נשכחת. הזמנו אותנו לאירוע שלך ונהפוך אותו למשהו מיוחד!
    `,
    content_en: `
# Weddings, Bar Mitzvahs and Events: Ideas for Unforgettable Photos

Your event should be special, and the photos should reflect the magic of the moment. In this guide, we'll present creative ideas to turn any event into an unforgettable photographic experience.

## Weddings: Moments of Love

### Character Ideas
- **Royal Bride and Groom** - Golden hair, crowns, royal attire
- **Fairy Tale Couple** - Prince and princess from love stories
- **Movie Stars** - Famous couple from old Hollywood
- **Futuristic Couple** - Space suits, sci-fi style

### Recommended Times for Photos
- **Wedding Ceremony** - Before or after the ceremony
- **Guest Reception** - When guests arrive
- **First Dance** - Romantic moment
- **Cake Cutting** - Moment of joy

## Bar Mitzvahs: Step into Adulthood

### Suitable Characters
- **Jewish Warrior** - King David, the Maccabees
- **Talmudic Sage** - Rambam, the Gaon of Vilna
- **Sports Hero** - Soccer player, Olympic swimmer
- **Rock Star** - Guitarist, band lead singer

### Special Ideas
- **Group Photos** - With all friends as different characters
- **Family** - Whole family in matching characters
- **Teachers** - Thank you to teachers in special characters

## Social Events: Fun and Entertainment

### Company Events
- **Superhero Team** - Each team member as a different hero
- **Movie Stars** - Team as famous celebrities
- **Professional Characters** - Doctors, lawyers, engineers

### Birthdays
- **Children** - Superheroes, princesses, movie characters
- **Adults** - Teen stars, nostalgic characters
- **Golden Age** - Past stars, classic characters

## Success Tips

### Advance Preparation
- Choose characters that fit the event
- Prepare guest list
- Plan the timing

### During the Event
- Guide the guests
- Take many photos
- Pay attention to details

### After the Event
- Share the photos
- Create a special album
- Preserve the memories

## Conclusion

With these ideas, your event will become an unforgettable photographic experience. Invite us to your event and we'll make it something special!
    `
  }
];

// Combine all blog posts
const allBlogPosts = [...blogPosts, ...additionalBlogPosts];

export default function BlogPost() {
  const { id } = useParams();
  const { language, isRTL } = useLanguage();
  
  const post = allBlogPosts.find(p => p.id === parseInt(id));
  
  if (!post) {
    return (
      <div className="min-h-screen py-16 text-center">
        <h1 className="text-3xl font-bold text-white mb-4">
          {language === 'he' ? 'המאמר לא נמצא' : 'Article Not Found'}
        </h1>
        <Link to={createPageUrl('Blog')}>
          <Button className="bg-violet-600 hover:bg-violet-700">
            {language === 'he' ? 'חזור לבלוג' : 'Back to Blog'}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link 
            to={createPageUrl('Blog')}
            className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors"
          >
            <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            {language === 'he' ? 'חזור לבלוג' : 'Back to Blog'}
          </Link>
        </motion.div>

        {/* Article Header */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl overflow-hidden"
        >
          {/* Hero Image */}
          <div className="relative h-64 md:h-96">
            <img 
              src={post.image} 
              alt={language === 'he' ? post.title_he : post.title_en}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 rounded-full bg-violet-600/90 text-white text-xs font-medium">
                  {language === 'he' ? post.category : post.category_en}
                </span>
                {post.featured && (
                  <div className="flex items-center gap-1">
                    <Sparkles className="w-4 h-4 text-yellow-400" />
                    <span className="text-yellow-400 text-xs font-medium">
                      {language === 'he' ? 'מומלץ' : 'Featured'}
                    </span>
                  </div>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                {language === 'he' ? post.title_he : post.title_en}
              </h1>
              <p className="text-slate-300 text-lg">
                {language === 'he' ? post.excerpt_he : post.excerpt_en}
              </p>
            </div>
          </div>

          {/* Article Meta */}
          <div className="p-6 border-b border-slate-800">
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-violet-600/20 flex items-center justify-center">
                  <User className="w-4 h-4 text-violet-400" />
                </div>
                <span className="text-slate-300">
                  {language === 'he' ? post.author : post.author_en}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{new Date(post.date).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US')}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{language === 'he' ? post.readTime : post.readTime_en}</span>
              </div>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 md:p-8">
            <div 
              className="prose prose-invert max-w-none text-slate-300 leading-relaxed"
              dangerouslySetInnerHTML={{ 
                __html: (language === 'he' ? post.content_he : post.content_en)
                  .replace(/\n/g, '<br>')
                  .replace(/#{1,6}\s/g, match => {
                    const level = match.trim().length;
                    return `<h${level} class="text-white font-bold mb-4 mt-6">`;
                  })
                  .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                  .replace(/\*(.*?)\*/g, '<em class="text-violet-400">$1</em>')
              }}
            />
          </div>

          {/* Engagement Bar */}
          <div className="p-6 border-t border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors">
                  <Heart className="w-5 h-5" />
                  <span>{post.likes}</span>
                </button>
                <button className="flex items-center gap-2 text-slate-400 hover:text-blue-400 transition-colors">
                  <MessageCircle className="w-5 h-5" />
                  <span>{post.comments}</span>
                </button>
                <button className="flex items-center gap-2 text-slate-400 hover:text-green-400 transition-colors">
                  <Share2 className="w-5 h-5" />
                  <span>{language === 'he' ? 'שתף' : 'Share'}</span>
                </button>
              </div>
              
              <Link to={createPageUrl('Photobooth')}>
                <Button className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white">
                  <Camera className="w-4 h-4 mr-2" />
                  {language === 'he' ? 'נסה בעצמך' : 'Try It Yourself'}
                </Button>
              </Link>
            </div>
          </div>
        </motion.article>

        {/* Related Posts */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-12"
        >
          <h2 className="text-2xl font-bold text-white mb-6">
            {language === 'he' ? 'מאמרים קשורים' : 'Related Articles'}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {allBlogPosts
              .filter(p => p.id !== post.id)
              .slice(0, 2)
              .map(relatedPost => (
                <Link 
                  key={relatedPost.id} 
                  to={`${createPageUrl('Blog')}/${relatedPost.id}`}
                  className="glass-card rounded-xl p-6 hover:border-violet-500/50 transition-all"
                >
                  <div className="flex gap-4">
                    <img 
                      src={relatedPost.image} 
                      alt={language === 'he' ? relatedPost.title_he : relatedPost.title_en}
                      className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                    />
                    <div className="flex-1">
                      <span className="text-xs text-violet-400 font-medium">
                        {language === 'he' ? relatedPost.category : relatedPost.category_en}
                      </span>
                      <h3 className="text-white font-semibold mt-1 mb-2 line-clamp-2">
                        {language === 'he' ? relatedPost.title_he : relatedPost.title_en}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{new Date(relatedPost.date).toLocaleDateString()}</span>
                        <span>•</span>
                        <span>{language === 'he' ? relatedPost.readTime : relatedPost.readTime_en}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
